import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";

const STORAGE_KEY = "feo-cookie-consent-v1";
const TTL_DAYS = 365;

type Consent = { value: "accepted"; ts: number };

function read(): Consent | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Consent;
    const ageDays = (Date.now() - parsed.ts) / (1000 * 60 * 60 * 24);
    if (ageDays > TTL_DAYS) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function CookieConsent() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Não mostrar dentro do editor/preview do Lovable
    if (typeof window !== "undefined" && /lovable\.(app|dev)$/.test(window.location.hostname)) {
      return;
    }
    // Não mostrar em rotas de embed (iframe em sites externos)
    if (typeof window !== "undefined" && window.location.pathname.startsWith("/embed/")) {
      return;
    }
    if (!read()) {
      const t = window.setTimeout(() => setVisible(true), 600);
      return () => window.clearTimeout(t);
    }
  }, []);


  if (!visible) return null;

  const accept = () => {
    try {
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ value: "accepted", ts: Date.now() } satisfies Consent),
      );
    } catch {
      /* ignore */
    }
    setVisible(false);
  };

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Aviso de cookies"
      data-cookie-consent className="fixed inset-x-3 bottom-3 z-[60] flex justify-center animate-in fade-in slide-in-from-bottom-2 sm:inset-x-0 sm:left-1/2 sm:right-auto sm:-translate-x-1/2"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      <div className="flex w-full items-center gap-3 rounded-2xl border border-white/10 bg-neutral-900/80 backdrop-blur-xl backdrop-saturate-150 shadow-[0_8px_32px_-8px_rgba(0,0,0,0.5)] ring-1 ring-white/[0.06] px-3 py-2.5 sm:w-auto sm:max-w-md sm:rounded-full sm:gap-3 sm:pl-4 sm:pr-1.5 sm:py-1.5">
        <p className="flex-1 text-xs leading-snug text-neutral-200 sm:whitespace-nowrap sm:flex-initial">
          Usamos cookies para melhorar sua experiência.{" "}
          <Link
            to="/politica-de-privacidade"
            className="underline underline-offset-2 hover:text-white"
          >
            Saiba mais
          </Link>
        </p>
        <button
          type="button"
          onClick={accept}
          className="shrink-0 rounded-full bg-white/95 px-4 py-2 text-xs font-semibold text-neutral-900 hover:bg-white transition-colors sm:px-3.5 sm:py-1.5"
        >
          Aceitar
        </button>
      </div>
    </div>
  );
}
